import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, SelectProps, Typography } from 'antd';
import { t } from 'i18next';
import { useSelector } from 'react-redux';

import { HoursSelector } from 'shared/FormItems/HoursSelector';

import { selectWorkTime } from 'features/config';

import { TimePriceFormProps, RequiredFormValues } from '../types';


type Props<FormValues> = {
  selectProps?: SelectProps;
  formProps: TimePriceFormProps<FormValues>;
};

export const SelectTimePrice = <FormValues extends RequiredFormValues,>
  ({
    formProps: { initialValue: formInitialValue, ...formProps }
  }: Props<FormValues>) => {
  const formTimesPrice = Form.useWatch(['metainfo', 'pricing'], formProps.form);
  const workTime = useSelector(selectWorkTime);
  const initialValue = formInitialValue ?? [{
    startTime: workTime.open,
    endTime: workTime.closed,
    price: 1,
  }];
  const timesPrice = formTimesPrice ?? initialValue;
  return (
    <Form.List
      {...formProps}
      name={['metainfo', 'pricing']}
      {...(formInitialValue === undefined && { initialValue })}
    >
      {(limitsFields, { add: addLimit, remove: removeLimit }) => (
        <>
          <Typography.Paragraph type="secondary">{t('admin-extensions.pricing-hint')}</Typography.Paragraph>
          {limitsFields.map(({ key, name, ...restField }, fieldIndex) => {
            const previous = timesPrice[fieldIndex - 1];
            const currentTimePrice = timesPrice[fieldIndex] ?? { startTime: previous?.endTime ?? workTime.open, endTime: workTime.closed, price: 1 };
            const priceStartTimeFrom = fieldIndex === 0
              ? workTime.open : previous?.endTime ?? workTime.open;
            const priceStartTimeTo = currentTimePrice.endTime - 1;
            const priceOverTimeFrom = currentTimePrice.startTime + 1;
            const priceOverTimeTo = workTime.closed;
            const isThisTimePriceActive = fieldIndex === timesPrice.length - 1;

            return (
              <div key={key}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <HoursSelector
                    from={priceStartTimeFrom}
                    to={priceStartTimeTo}
                    formProps={{
                      ...restField,
                      name: [name, 'startTime'],
                      label: t('form-items.select.time-space.start'),
                      style: { width: '45%' }
                    }}
                    selectProps={{
                      disabled: !isThisTimePriceActive,
                    }}
                  />
                  <HoursSelector
                    from={priceOverTimeFrom}
                    to={priceOverTimeTo}
                    formProps={{
                      ...restField,
                      name: [name, 'endTime'],
                      label: t('form-items.select.time-space.end'),
                      style: { width: '45%' }
                    }}
                    selectProps={{
                      disabled: !isThisTimePriceActive,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: '16px'
                  }}
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'price']}
                    label={t('form-items.input.hour-price')}
                  >
                    <InputNumber min={1} max={100000} />
                  </Form.Item>
                  {
                    fieldIndex === limitsFields.length - 1 &&
                    fieldIndex !== 0 &&
                    <Button
                      onClick={() => removeLimit(name)}
                      style={{ marginBottom: '5px' }}
                      danger
                    >
                      {t('form-items.btn.delete-times-price')}
                    </Button>
                  }
                </div>
              </div>
            );
          })}
          {
            timesPrice &&
            (timesPrice.length === 0 || timesPrice[timesPrice.length - 1]?.endTime < workTime.closed) &&
            (
              <Form.Item style={{ marginTop: '8px' }}>
                <Button type="dashed" onClick={() => addLimit({ startTime: timesPrice[timesPrice.length - 1]?.endTime ?? workTime.open, endTime: workTime.closed, price: 1 })} block icon={<PlusOutlined />}>
                  {t('form-items.btn.add-times-price')}
                </Button>
              </Form.Item>
            )
          }
        </>
      )}
    </Form.List >
  );
};
